// Money formatting for the platform. Nakabh sells on Amazon.in, so amounts are
// rupees and are grouped the Indian way (₹1,23,456 — lakh/crore), not ₹123,456.
//
// Single source of truth: pages must not build their own Intl.NumberFormat, or
// the dashboards drift apart again.

export const PLATFORM_CURRENCY = 'INR';
export const PLATFORM_LOCALE = 'en-IN';

const whole = new Intl.NumberFormat(PLATFORM_LOCALE, {
  style: 'currency',
  currency: PLATFORM_CURRENCY,
  maximumFractionDigits: 0,
});

const precise = new Intl.NumberFormat(PLATFORM_LOCALE, {
  style: 'currency',
  currency: PLATFORM_CURRENCY,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Rupees, no paise. Use for KPIs, totals, and table cells. */
export function fmtINR(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return whole.format(n);
}

/** Rupees with paise. Use for unit prices and per-item margins. */
export function fmtINRPrecise(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return precise.format(n);
}

/** Indian digit grouping without a currency symbol. */
export function fmtIndianNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat(PLATFORM_LOCALE).format(n);
}

/**
 * Compact rupees for dense cards: ₹1.2Cr / ₹3.4L / ₹5.6K.
 * Intl compact notation renders en-IN as "T"/"L"/"Cr" inconsistently across
 * engines, so the thresholds are applied explicitly.
 */
export function fmtINRCompact(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_00_00_000) return `${sign}₹${(abs / 1_00_00_000).toFixed(2)}Cr`;
  if (abs >= 1_00_000) return `${sign}₹${(abs / 1_00_000).toFixed(2)}L`;
  if (abs >= 1_000) return `${sign}₹${(abs / 1_000).toFixed(1)}K`;
  return `${sign}₹${abs.toFixed(0)}`;
}

/**
 * fmtUSD is kept as a deprecated alias so no call site silently renders a bare
 * number during the migration. It formats rupees; rename call sites to fmtINR.
 * @deprecated use fmtINR
 */
export const fmtUSD = fmtINR;
